/**
 * TestProgressModal Component
 *
 * Shows real-time progress when testing an automation rule.
 * Subscribes to execution status via useQuery and displays progress steps.
 */

import { memo, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { ITestProgressModalProps, AutomationExecutionStatus } from '@/@types/automation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, Circle, Loader2, AlertCircle, Send } from 'lucide-react';

/**
 * Progress step configuration
 */
interface IProgressStep {
  id: string;
  label: string;
  description?: string;
}

const PROGRESS_STEPS: IProgressStep[] = [
  { id: 'created', label: 'Test Started', description: 'Test run initiated' },
  { id: 'generating', label: 'Generating content', description: 'AI is creating your content' },
  {
    id: 'approval',
    label: 'Awaiting approval',
    description: 'Check Telegram for approval request',
  },
  { id: 'publishing', label: 'Publishing', description: 'Posting to social media' },
];

/**
 * Map execution status to completed steps
 */
function getCompletedSteps(status: AutomationExecutionStatus | undefined): Set<string> {
  const completed = new Set<string>();

  if (!status) return completed;

  // 'created' is always done once we have a status
  completed.add('created');

  // Based on status, mark steps as completed
  switch (status) {
    case 'pending':
      // Only created is done
      break;
    case 'generating':
      // Created done, generating in progress
      break;
    case 'awaiting_approval':
      // Created and generating done, approval in progress
      completed.add('generating');
      break;
    case 'approved':
      // Created, generating, approval done - publishing next
      completed.add('generating');
      completed.add('approval');
      break;
    case 'published':
      // All done
      completed.add('generating');
      completed.add('approval');
      completed.add('publishing');
      break;
    case 'failed':
    case 'skipped':
    case 'rejected':
      // Mark what was completed before failure
      if (status === 'rejected') {
        completed.add('generating');
        completed.add('approval');
      }
      break;
  }

  return completed;
}

/**
 * Get the currently active step
 */
function getActiveStep(status: AutomationExecutionStatus | undefined): string | null {
  if (!status) return null;

  switch (status) {
    case 'pending':
      return 'created';
    case 'generating':
      return 'generating';
    case 'awaiting_approval':
      return 'approval';
    case 'approved':
      return 'publishing';
    case 'published':
    case 'failed':
    case 'skipped':
    case 'rejected':
      return null; // No active step - finished
  }

  return null;
}

/**
 * Get status display text and color
 */
function getStatusDisplay(status: AutomationExecutionStatus | undefined): {
  text: string;
  color: string;
  icon: React.ReactNode;
} {
  switch (status) {
    case 'pending':
      return {
        text: 'Starting...',
        color: 'text-muted-foreground',
        icon: <Loader2 className="h-4 w-4 animate-spin" />,
      };
    case 'generating':
      return {
        text: 'Generating content...',
        color: 'text-blue-500',
        icon: <Loader2 className="h-4 w-4 animate-spin" />,
      };
    case 'awaiting_approval':
      return {
        text: 'Waiting for your approval in Telegram',
        color: 'text-yellow-500',
        icon: <Send className="h-4 w-4" />,
      };
    case 'approved':
      return {
        text: 'Approved - publishing...',
        color: 'text-green-500',
        icon: <Loader2 className="h-4 w-4 animate-spin" />,
      };
    case 'published':
      return {
        text: 'Published successfully!',
        color: 'text-green-500',
        icon: <Check className="h-4 w-4" />,
      };
    case 'failed':
      return {
        text: 'Test failed',
        color: 'text-red-500',
        icon: <AlertCircle className="h-4 w-4" />,
      };
    case 'skipped':
      return {
        text: 'Skipped',
        color: 'text-muted-foreground',
        icon: <Circle className="h-4 w-4" />,
      };
    case 'rejected':
      return {
        text: 'Regeneration requested',
        color: 'text-yellow-500',
        icon: <AlertCircle className="h-4 w-4" />,
      };
    default:
      return {
        text: 'Loading...',
        color: 'text-muted-foreground',
        icon: <Loader2 className="h-4 w-4 animate-spin" />,
      };
  }
}

export const TestProgressModal = memo(function TestProgressModal({
  isOpen,
  onClose,
  executionId,
  ruleName,
}: ITestProgressModalProps) {
  // Subscribe to execution status
  const execution = useQuery(api.automation.executions.get, executionId ? { executionId } : 'skip');

  const completedSteps = useMemo(() => getCompletedSteps(execution?.status), [execution?.status]);

  const activeStep = useMemo(() => getActiveStep(execution?.status), [execution?.status]);

  const statusDisplay = useMemo(() => getStatusDisplay(execution?.status), [execution?.status]);

  const isFinished =
    execution?.status === 'published' ||
    execution?.status === 'failed' ||
    execution?.status === 'skipped';

  // Filter steps based on rule config (skip approval step if not needed)
  // For now, show all steps - the approval step will just be skipped quickly if not required
  const steps = PROGRESS_STEPS;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Test Automation</DialogTitle>
          <DialogDescription>Testing: {ruleName}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Progress Steps */}
          <div className="space-y-4">
            {steps.map((step, index) => {
              const isCompleted = completedSteps.has(step.id);
              const isActive = activeStep === step.id;
              const isPending = !isCompleted && !isActive;

              return (
                <div key={step.id} className="flex items-start gap-3">
                  {/* Step indicator */}
                  <div className="flex flex-col items-center">
                    <div
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-colors',
                        isCompleted && 'border-green-500 bg-green-500 text-white',
                        isActive && 'border-primary bg-primary/10 text-primary',
                        isPending && 'border-muted bg-muted/50 text-muted-foreground'
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : isActive ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Circle className="h-4 w-4" />
                      )}
                    </div>
                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div
                        className={cn('w-0.5 h-4 mt-1', isCompleted ? 'bg-green-500' : 'bg-muted')}
                      />
                    )}
                  </div>

                  {/* Step content */}
                  <div className="flex-1 pt-1">
                    <p
                      className={cn(
                        'text-sm font-medium',
                        isCompleted && 'text-green-600',
                        isActive && 'text-foreground',
                        isPending && 'text-muted-foreground'
                      )}
                    >
                      {step.label}
                    </p>
                    {isActive && step.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Status bar */}
          <div className="mt-6 pt-4 border-t">
            <div className={cn('flex items-center gap-2 text-sm', statusDisplay.color)}>
              {statusDisplay.icon}
              <span>{statusDisplay.text}</span>
            </div>

            {/* Error message */}
            {execution?.status === 'failed' && execution.errorMessage && (
              <p className="mt-2 text-xs text-red-500 bg-red-50 dark:bg-red-950/20 p-2 rounded">
                {execution.errorMessage}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant={isFinished ? 'default' : 'outline'}>
            {isFinished ? 'Done' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
